import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { BrIconComponent } from '../../../shared/ui/icon/icon.component';
import {
  DynamicColumn,
  DynamicWorkflowNode,
  DynamicWorkflowTransition,
} from './dynamic-framework.models';
import {
  autoLayout,
  GRID,
  NODE_HEIGHT,
  NODE_WIDTH,
  NodePosition,
  snap,
  WorkflowLayout,
} from './dynamic-workflow-layout';
import { screenToWorld, WorkflowCamera, zoomCameraAt } from './dynamic-workflow-camera';

interface CanvasNode {
  node: DynamicWorkflowNode;
  x: number;
  y: number;
  height: number;
}

interface CanvasEdge {
  transition: DynamicWorkflowTransition;
  index: number;
  path: string;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

type TransitionEndpoint = 'from' | 'to';

type LinkInteraction = {
  kind: 'create';
  pointerId: number;
  fromNodeKey: string;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
} | {
  kind: 'rewire';
  pointerId: number;
  edgeIndex: number;
  endpoint: TransitionEndpoint;
  fromNodeKey: string;
  toNodeKey: string;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
};

type PointerInteraction = {
  kind: 'move-node';
  pointerId: number;
  key: string;
  dx: number;
  dy: number;
  startClientX: number;
  startClientY: number;
  startPosition: NodePosition;
  moved: boolean;
} | {
  kind: 'pan';
  pointerId: number;
  startClientX: number;
  startClientY: number;
  startPanX: number;
  startPanY: number;
  clearSelectionOnClick: boolean;
  moved: boolean;
} | LinkInteraction;

const MIN_SCALE = 0.4;
const MAX_SCALE = 1.5;
const SCALE_STEP = 0.1;
const MIN_PAD = 48;
const FIELD_ROW_HEIGHT = 28;
const MAX_VISIBLE_FIELD_ROWS = 5;
const FIELD_LIST_SPACING = 8;
const DRAG_THRESHOLD = 2;

@Component({
  selector: 'app-dynamic-workflow-graph',
  standalone: true,
  imports: [CommonModule, BrIconComponent],
  templateUrl: './dynamic-workflow-graph.component.html',
  styleUrls: ['./dynamic-workflow-graph.component.css'],
})
export class DynamicWorkflowGraphComponent implements OnChanges, AfterViewInit, OnDestroy {
  @Input() nodes: DynamicWorkflowNode[] = [];
  @Input() transitions: DynamicWorkflowTransition[] = [];
  @Input() layout: WorkflowLayout = {};
  @Input() readOnly = false;
  @Input() showControls = true;
  @Input() selectedNodeKey: string | null = null;
  @Input() selectedTransitionIndex: number | null = null;
  @Input() errorNodeKeys = new Set<string>();

  @Output() nodeSelected = new EventEmitter<string>();
  @Output() transitionSelected = new EventEmitter<number>();
  @Output() selectionCleared = new EventEmitter<void>();
  @Output() nodeMoved = new EventEmitter<{ key: string; position: NodePosition }>();
  @Output() transitionCreated = new EventEmitter<{ fromNodeKey: string; toNodeKey: string }>();
  @Output() transitionReconnected = new EventEmitter<{
    index: number;
    fromNodeKey: string;
    toNodeKey: string;
  }>();

  @ViewChild('surface') surface?: ElementRef<HTMLElement>;

  readonly nodeWidth = NODE_WIDTH;

  canvasNodes: CanvasNode[] = [];
  canvasEdges: CanvasEdge[] = [];
  readonly expandedNodeKeys = new Set<string>();

  /** Canvas zoom. Collapsed nodes keep the compact 204x78 footprint; an
   *  expanded field list grows only its own node. */
  scale = 1;
  panX = 0;
  panY = 0;

  /** Persistent canvas-pan tool. Space remains the temporary shortcut. */
  moveMode = false;

  /** Space is held: the canvas is grabbed, and any press pans it. */
  space = false;
  pointerInside = false;

  /** Live positions. Mirrors `layout`, but a drag writes here every frame so
   *  the node and its edges move together without a round trip to the parent. */
  private positions: WorkflowLayout = {};
  private interaction: PointerInteraction | null = null;
  /** The viewport origin in client space, read once when a gesture starts. */
  private origin: NodePosition | null = null;

  constructor(private readonly zone: NgZone, private readonly cdr: ChangeDetectorRef) {}

  ngAfterViewInit(): void {
    const surface = this.surface?.nativeElement;
    if (!surface) return;
    // Outside Angular: a drag emits a pointer event per frame, and each one
    // inside the zone ran change detection over the whole application. The
    // handlers redraw this component themselves and re-enter the zone only
    // when a gesture actually produces a result worth telling the parent.
    this.zone.runOutsideAngular(() => {
      window.addEventListener('pointermove', this.handleMove);
      window.addEventListener('pointerup', this.handleUp);
      window.addEventListener('pointercancel', this.handleCancel);
      surface.addEventListener('wheel', this.handleWheel, { passive: false });
      document.addEventListener('keydown', this.handleKeyDown);
      document.addEventListener('keyup', this.handleKeyUp);
      window.addEventListener('blur', this.handleBlur);
    });
  }

  ngOnDestroy(): void {
    const surface = this.surface?.nativeElement;
    window.removeEventListener('pointermove', this.handleMove);
    window.removeEventListener('pointerup', this.handleUp);
    window.removeEventListener('pointercancel', this.handleCancel);
    surface?.removeEventListener('wheel', this.handleWheel);
    document.removeEventListener('keydown', this.handleKeyDown);
    document.removeEventListener('keyup', this.handleKeyUp);
    window.removeEventListener('blur', this.handleBlur);
  }

  /**
   * Space holds the canvas. While it is down every press pans, wherever the
   * pointer is - over a node, over an edge, anywhere. Panning only from bare
   * canvas is no use on a graph dense enough to need panning, which is the
   * only kind that needs it.
   */
  private readonly handleKeyDown = (event: KeyboardEvent): void => {
    if (event.code !== 'Space' || event.repeat || !this.pointerInside
      || isInteractive(event.target)) return;
    event.preventDefault();
    if (this.interactionActive) return;
    if (this.space) return;
    this.space = true;
    this.cdr.detectChanges();
  };

  private readonly handleKeyUp = (event: Event): void => {
    if (event instanceof KeyboardEvent && event.code !== 'Space') return;
    if (!this.space) return;
    this.space = false;
    this.cdr.detectChanges();
  };

  /** Ctrl/Cmd + wheel zooms about the pointer. A plain wheel is left alone so
   *  the surface scrolls the way every other scrollable box does. */
  private readonly handleWheel = (event: WheelEvent): void => {
    if (!event.ctrlKey && !event.metaKey) return;
    event.preventDefault();
    if (this.interactionActive) return;
    this.zone.run(() => this.zoomAbout(
      event.deltaY < 0 ? SCALE_STEP : -SCALE_STEP,
      event.clientX,
      event.clientY,
    ));
  };

  private readonly handleMove = (event: PointerEvent): void => {
    if (!this.matchesInteraction(event)) return;
    this.onPointerMove(event);
    this.cdr.detectChanges();
  };

  private readonly handleUp = (event: PointerEvent): void => {
    if (!this.matchesInteraction(event)) return;
    this.zone.run(() => {
      this.onPointerUp(event);
      this.cdr.detectChanges();
    });
  };

  private readonly handleCancel = (event: PointerEvent): void => {
    if (!this.matchesInteraction(event)) return;
    this.zone.run(() => {
      this.cancelInteraction();
      this.cdr.detectChanges();
    });
  };

  private readonly handleBlur = (): void => {
    if (!this.space && !this.interactionActive) return;
    this.zone.run(() => {
      this.space = false;
      this.cancelInteraction();
      this.cdr.detectChanges();
    });
  };

  /** The node under the pointer while connecting, and whether dropping on it
   *  would actually make a transition. */
  hoverNodeKey: string | null = null;
  hoverInvalid = false;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['nodes'] || changes['transitions'] || changes['layout']) {
      this.cancelInteraction();
      this.positions = { ...autoLayout(this.nodes, this.transitions), ...this.layout };
    }
    if (changes['nodes']) {
      const expandable = new Set(this.nodes.filter(node => node.fields.length).map(node => node.key));
      for (const key of this.expandedNodeKeys) {
        if (!expandable.has(key)) this.expandedNodeKeys.delete(key);
      }
    }
    this.render();
  }

  get zoomPercent(): number {
    return Math.round(this.scale * 100);
  }

  /** Keep the viewport grid aligned with the transformed graph without
   * transforming (and therefore shrinking) the interactive surface itself. */
  get gridSize(): string {
    const size = GRID * this.scale;
    return `${size}px ${size}px`;
  }

  get canZoomIn(): boolean {
    return !this.interactionActive && this.scale < MAX_SCALE;
  }

  get canZoomOut(): boolean {
    return !this.interactionActive && this.scale > MIN_SCALE;
  }

  get panning(): boolean {
    return this.interaction?.kind === 'pan';
  }

  get draggingKey(): string | null {
    return this.interaction?.kind === 'move-node' ? this.interaction.key : null;
  }

  get interactionActive(): boolean {
    return this.interaction !== null;
  }

  get link(): LinkInteraction | null {
    return this.interaction?.kind === 'create' || this.interaction?.kind === 'rewire'
      ? this.interaction
      : null;
  }

  get selectedEdgeHandles(): Pick<CanvasEdge, 'x1' | 'y1' | 'x2' | 'y2'> | null {
    if (this.link?.kind === 'rewire'
      && this.link.edgeIndex === this.selectedTransitionIndex) {
      return this.link;
    }
    if (this.selectedTransitionIndex == null) return null;
    return this.canvasEdges.find(edge => edge.index === this.selectedTransitionIndex) ?? null;
  }

  toggleMoveMode(): void {
    if (this.interactionActive) return;
    this.moveMode = !this.moveMode;
  }

  zoomIn(): void {
    this.zoomAboutViewportCenter(SCALE_STEP);
  }

  zoomOut(): void {
    this.zoomAboutViewportCenter(-SCALE_STEP);
  }

  /** Places a collapsed node at the centre of what the user can currently see. */
  newNodePositionAtViewportCenter(): NodePosition | null {
    const surface = this.surface?.nativeElement;
    if (!surface) return null;
    const box = surface.getBoundingClientRect();
    const centre = screenToWorld(
      {
        x: box.left + surface.clientWidth / 2,
        y: box.top + surface.clientHeight / 2,
      },
      { x: box.left, y: box.top },
      this.camera,
    );
    return {
      x: snap(centre.x - NODE_WIDTH / 2),
      y: snap(centre.y - NODE_HEIGHT / 2),
    };
  }

  /** Scales so the whole graph is on screen, never magnifying past 1: a small
   *  graph should sit at its natural size rather than fill the viewport. */
  zoomToFit(): void {
    if (this.interactionActive) return;
    const box = this.surface?.nativeElement;
    if (!box || !this.canvasNodes.length) {
      this.scale = 1;
      this.panX = 0;
      this.panY = 0;
      return;
    }
    const left = Math.min(...this.canvasNodes.map(item => item.x));
    const top = Math.min(...this.canvasNodes.map(item => item.y));
    const right = Math.max(...this.canvasNodes.map(item => item.x + NODE_WIDTH));
    const bottom = Math.max(...this.canvasNodes.map(item => item.y + item.height));
    const width = Math.max(right - left, 1);
    const height = Math.max(bottom - top, 1);
    this.scale = this.clampScale(Math.min(
      1,
      (box.clientWidth - MIN_PAD * 2) / width,
      (box.clientHeight - MIN_PAD * 2) / height,
    ));
    this.panX = (box.clientWidth - width * this.scale) / 2 - left * this.scale;
    this.panY = (box.clientHeight - height * this.scale) / 2 - top * this.scale;
  }

  private clampScale(value: number): number {
    return Math.min(MAX_SCALE, Math.max(MIN_SCALE, Math.round(value * 100) / 100));
  }

  private zoomAboutViewportCenter(delta: number): void {
    const surface = this.surface?.nativeElement;
    if (!surface) return;
    const box = surface.getBoundingClientRect();
    this.zoomAbout(
      delta,
      box.left + surface.clientWidth / 2,
      box.top + surface.clientHeight / 2,
    );
  }

  /**
   * Zooms while keeping the model point under the pointer where it is.
   * Scaling about the canvas origin instead sends whatever the user was
   * looking at sliding off, which is what makes zoom feel like teleporting.
   */
  private zoomAbout(delta: number, clientX: number, clientY: number): void {
    if (this.interactionActive) return;
    const surface = this.surface?.nativeElement;
    if (!surface) return;
    this.captureOrigin();
    if (!this.origin) return;
    const nextScale = this.clampScale(this.scale + delta);
    if (nextScale === this.scale) return;
    const next = zoomCameraAt(
      this.camera,
      nextScale,
      { x: clientX, y: clientY },
      this.origin,
    );
    this.scale = next.scale;
    this.panX = next.panX;
    this.panY = next.panY;
  }

  trackNode(_: number, item: CanvasNode): string {
    return item.node.key;
  }

  trackEdge(_: number, item: CanvasEdge): number {
    return item.index;
  }

  isNodeExpanded(key: string): boolean {
    return this.expandedNodeKeys.has(key);
  }

  toggleNodeFields(event: Event, key: string): void {
    event.stopPropagation();
    if (this.expandedNodeKeys.has(key)) this.expandedNodeKeys.delete(key);
    else this.expandedNodeKeys.add(key);
    this.render();
  }

  fieldMeta(field: DynamicColumn): string {
    const type = {
      shortText: 'Text',
      number: 'Number',
      date: 'Date',
      boolean: 'Yes / No',
      choice: 'Choice',
    }[field.dataType];
    return type;
  }

  // --- pointer interaction -------------------------------------------------

  onNodePointerDown(event: PointerEvent, item: CanvasNode): void {
    event.stopPropagation();
    if (this.shouldPan(event)) {
      this.startPan(event);
      return;
    }
    if (event.button !== 0 || this.interactionActive) return;
    if (this.readOnly) {
      this.nodeSelected.emit(item.node.key);
      return;
    }
    this.captureOrigin();
    const point = this.toCanvas(event);
    this.beginInteraction(event, {
      kind: 'move-node',
      pointerId: event.pointerId,
      key: item.node.key,
      dx: point.x - item.x,
      dy: point.y - item.y,
      startClientX: event.clientX,
      startClientY: event.clientY,
      startPosition: { x: item.x, y: item.y },
      moved: false,
    });
    this.nodeSelected.emit(item.node.key);
  }

  /** Interactive content inside a node does not start a node drag. Middle
   *  mouse still pans; Move and Space pass through to the node via CSS. */
  onNodeContentPointerDown(event: PointerEvent, item: CanvasNode): void {
    event.stopPropagation();
    if (this.shouldPan(event)) {
      this.startPan(event);
      return;
    }
    if (event.button === 0 && !this.interactionActive) {
      this.nodeSelected.emit(item.node.key);
    }
  }

  onConnectorPointerDown(event: PointerEvent, item: CanvasNode): void {
    event.stopPropagation();
    if (this.shouldPan(event)) {
      this.startPan(event);
      return;
    }
    if (this.readOnly || event.button !== 0 || this.interactionActive) return;
    this.selectionCleared.emit();
    this.captureOrigin();
    const from = { x: item.x + NODE_WIDTH, y: item.y + item.height / 2 };
    const point = this.toCanvas(event);
    this.beginInteraction(event, {
      kind: 'create',
      pointerId: event.pointerId,
      fromNodeKey: item.node.key,
      x1: from.x,
      y1: from.y,
      x2: point.x,
      y2: point.y,
    });
  }

  onTransitionEndpointPointerDown(
    event: PointerEvent,
    index: number | null,
    endpoint: TransitionEndpoint,
  ): void {
    event.stopPropagation();
    if (this.shouldPan(event)) {
      this.startPan(event);
      return;
    }
    if (this.readOnly || event.button !== 0 || index == null || this.interactionActive) return;

    const edge = this.canvasEdges.find(item => item.index === index);
    if (!edge) return;
    this.captureOrigin();
    const point = this.toCanvas(event);
    this.beginInteraction(event, {
      kind: 'rewire',
      pointerId: event.pointerId,
      edgeIndex: index,
      endpoint,
      fromNodeKey: edge.transition.fromNodeKey,
      toNodeKey: edge.transition.toNodeKey,
      x1: endpoint === 'from' ? point.x : edge.x1,
      y1: endpoint === 'from' ? point.y : edge.y1,
      x2: endpoint === 'to' ? point.x : edge.x2,
      y2: endpoint === 'to' ? point.y : edge.y2,
    });
    this.selectEdge(index);
  }

  private beginInteraction(event: PointerEvent, interaction: PointerInteraction): void {
    if (this.interactionActive) return;
    this.interaction = interaction;
    this.capture(event);
    event.preventDefault();
  }

  /** Capture on the pressed element when supported. Window-level move and up
   *  handlers keep the gesture alive if the pointer leaves that element. */
  private capture(event: PointerEvent): void {
    const target = event.currentTarget;
    if (!(target instanceof Element) || !target.setPointerCapture) return;
    try {
      target.setPointerCapture(event.pointerId);
    } catch {
      // Window-level move/up listeners keep the gesture alive when a browser
      // cannot capture a synthetic or already-released pointer.
    }
  }

  onPointerMove(event: PointerEvent): void {
    const interaction = this.interaction;
    if (!interaction || interaction.pointerId !== event.pointerId) return;

    if (interaction.kind === 'move-node') {
      // Follow the pointer exactly. Snapping every frame made the node travel
      // in 16px jumps while the cursor moved smoothly, and let the two drift
      // up to half a step apart - which is what a drag "not tracking" is. The
      // grid is applied once, on release, so the node still lands on it.
      const moved = interaction.moved || movedPastThreshold(
        interaction.startClientX,
        interaction.startClientY,
        event,
      );
      if (!moved) return;
      const point = this.toCanvas(event);
      const next = {
        x: point.x - interaction.dx,
        y: point.y - interaction.dy,
      };
      const current = this.positions[interaction.key];
      if (!current || current.x !== next.x || current.y !== next.y) {
        interaction.moved = true;
        this.positions[interaction.key] = next;
        this.render();
      }
      return;
    }
    if (interaction.kind === 'pan') {
      const moved = interaction.moved || movedPastThreshold(
        interaction.startClientX,
        interaction.startClientY,
        event,
      );
      if (!moved) return;
      interaction.moved = true;
      this.panX = interaction.startPanX + event.clientX - interaction.startClientX;
      this.panY = interaction.startPanY + event.clientY - interaction.startClientY;
      return;
    }
    if (interaction.kind === 'create' || interaction.kind === 'rewire') {
      const point = this.toCanvas(event);
      this.interaction = interaction.kind === 'rewire' && interaction.endpoint === 'from'
        ? { ...interaction, x1: point.x, y1: point.y }
        : { ...interaction, x2: point.x, y2: point.y };
      const link = this.link!;
      // Say, before the user commits, whether this drop lands anywhere and
      // whether it would be accepted.
      const target = this.nodeAt(point);
      this.hoverNodeKey = target?.node.key ?? null;
      this.hoverInvalid = !!target && !!this.linkRefusal(
        this.linkFromKey(target.node.key, link),
        this.linkToKey(target.node.key, link),
        link.kind === 'rewire' ? link.edgeIndex : null,
      );
    }
  }

  onPointerUp(event: PointerEvent): void {
    const interaction = this.interaction;
    if (!interaction || interaction.pointerId !== event.pointerId) return;
    const dropPoint = interaction.kind === 'create' || interaction.kind === 'rewire'
      ? this.toCanvas(event)
      : null;
    this.interaction = null;
    this.origin = null;

    if (interaction.kind === 'move-node') {
      const { key, moved, startPosition } = interaction;
      if (!moved) return;
      const free = this.positions[key];
      const landed = { x: snap(free.x), y: snap(free.y) };
      this.positions[key] = landed;
      this.render();
      if (landed.x === startPosition.x && landed.y === startPosition.y) return;
      this.nodeMoved.emit({ key, position: landed });
      return;
    }
    if (interaction.kind === 'pan') {
      if (!interaction.moved && interaction.clearSelectionOnClick) {
        this.selectionCleared.emit();
      }
      return;
    }
    this.hoverNodeKey = null;
    this.hoverInvalid = false;

    const target = dropPoint ? this.nodeAt(dropPoint) : undefined;
    if (!target) return;
    const from = this.linkFromKey(target.node.key, interaction);
    const to = this.linkToKey(target.node.key, interaction);
    const refusal = this.linkRefusal(
      from,
      to,
      interaction.kind === 'rewire' ? interaction.edgeIndex : null,
    );
    if (refusal) return;

    if (interaction.kind === 'create') {
      this.transitionCreated.emit({ fromNodeKey: from, toNodeKey: to });
      return;
    }
    this.transitionReconnected.emit({
      index: interaction.edgeIndex,
      fromNodeKey: from,
      toNodeKey: to,
    });
  }

  cancelInteraction(): void {
    const interaction = this.interaction;
    if (!interaction) return;
    this.interaction = null;
    this.origin = null;
    this.hoverNodeKey = null;
    this.hoverInvalid = false;
    if (interaction.kind === 'move-node') {
      this.positions[interaction.key] = interaction.startPosition;
      this.render();
    } else if (interaction.kind === 'pan') {
      this.panX = interaction.startPanX;
      this.panY = interaction.startPanY;
    }
  }

  /** Bare-canvas clicks clear selection. The Move tool, Space, or the middle
   * mouse button turns the same gesture into a canvas pan. */
  onCanvasPointerDown(event: PointerEvent): void {
    const target = event.target as Element;
    const isBackground = event.target === event.currentTarget
      || target.classList.contains('graph-canvas')
      || target.classList.contains('graph-edges');
    if (!isBackground) return;
    if (this.shouldPan(event)) {
      this.startPan(event, true);
      return;
    }
    if (event.button === 0 && !this.interactionActive) this.selectionCleared.emit();
  }

  onEdgePointerDown(event: PointerEvent, index: number): void {
    event.stopPropagation();
    if (this.shouldPan(event)) {
      this.startPan(event);
      return;
    }
    if (event.button === 0 && !this.interactionActive) this.selectEdge(index);
  }

  private startPan(event: PointerEvent, clearSelectionOnClick = false): void {
    if (!this.surface || this.interactionActive) return;
    this.beginInteraction(event, {
      kind: 'pan',
      pointerId: event.pointerId,
      startClientX: event.clientX,
      startClientY: event.clientY,
      startPanX: this.panX,
      startPanY: this.panY,
      clearSelectionOnClick,
      moved: false,
    });
  }

  private shouldPan(event: PointerEvent): boolean {
    return this.moveMode || this.space || event.button === 1;
  }

  private matchesInteraction(event: PointerEvent): boolean {
    return this.interaction?.pointerId === event.pointerId;
  }

  selectEdge(index: number): void {
    this.transitionSelected.emit(index);
  }

  /** Reads the fixed viewport origin in client space once per gesture. */
  private captureOrigin(): void {
    const box = this.surface?.nativeElement.getBoundingClientRect();
    this.origin = box ? { x: box.left, y: box.top } : null;
  }

  /** Pointer position in model coordinates: the canvas is drawn at model size
   *  and scaled, so screen distance has to be divided back out by the zoom. */
  private toCanvas(event: PointerEvent): NodePosition {
    if (!this.origin) return { x: 0, y: 0 };
    return screenToWorld(
      { x: event.clientX, y: event.clientY },
      this.origin,
      this.camera,
    );
  }

  private get camera(): WorkflowCamera {
    return { panX: this.panX, panY: this.panY, scale: this.scale };
  }

  private nodeAt(point: NodePosition): CanvasNode | undefined {
    const matches = this.canvasNodes.filter(item =>
      point.x >= item.x && point.x <= item.x + NODE_WIDTH
      && point.y >= item.y && point.y <= item.y + item.height);
    return [...matches].reverse().find(item => this.expandedNodeKeys.has(item.node.key))
      ?? matches.at(-1);
  }

  /** Why dropping here would not make a transition, or null if it would. */
  private linkFromKey(candidateKey: string, gesture = this.link): string {
    if (!gesture) return candidateKey;
    return gesture.kind === 'rewire' && gesture.endpoint === 'from'
      ? candidateKey
      : gesture.fromNodeKey;
  }

  private linkToKey(candidateKey: string, gesture = this.link): string {
    if (!gesture || gesture.kind === 'create') return candidateKey;
    return gesture.endpoint === 'to' ? candidateKey : gesture.toNodeKey;
  }

  private linkRefusal(
    fromKey: string,
    toKey: string,
    ignoredEdgeIndex: number | null = null,
  ): string | null {
    if (fromKey === toKey) return 'A node cannot transition to itself.';
    if (this.transitions.some((edge, index) => index !== ignoredEdgeIndex
      && edge.fromNodeKey === fromKey
      && edge.toNodeKey === toKey)) {
      return 'That transition already exists.';
    }
    return null;
  }

  // --- rendering -----------------------------------------------------------

  private render(): void {
    this.canvasNodes = this.nodes.map((node, index) => {
      const position = this.positions[node.key] ?? { x: 48, y: 32 + index * 112 };
      return { node, x: position.x, y: position.y, height: this.heightFor(node) };
    });

    const byKey = new Map(this.canvasNodes.map(item => [item.node.key, item]));
    this.canvasEdges = this.transitions.flatMap((transition, index) => {
      const from = byKey.get(transition.fromNodeKey);
      const to = byKey.get(transition.toNodeKey);
      if (!from || !to) return [];
      return [{ transition, index, ...this.geometry(from, to) }];
    });

  }

  private heightFor(node: DynamicWorkflowNode): number {
    if (!node.fields.length || !this.expandedNodeKeys.has(node.key)) return NODE_HEIGHT;
    return NODE_HEIGHT
      + FIELD_LIST_SPACING
      + Math.min(node.fields.length, MAX_VISIBLE_FIELD_ROWS) * FIELD_ROW_HEIGHT;
  }

  /**
   * An edge leaves the right of its source and arrives at the left of its
   * target. When the target sits at or behind the source that curve would
   * double back through both cards, so a backward edge goes over the top
   * instead - the one case where the shape of the line carries meaning.
   */
  private geometry(from: CanvasNode, to: CanvasNode): Omit<CanvasEdge, 'transition' | 'index'> {
    const x1 = from.x + NODE_WIDTH;
    const y1 = from.y + from.height / 2;
    const x2 = to.x;
    const y2 = to.y + to.height / 2;

    if (x2 < x1 + 24) {
      const ax = from.x + NODE_WIDTH / 2;
      const bx = to.x + NODE_WIDTH / 2;
      const lift = Math.min(from.y, to.y) - 40;
      return {
        path: `M ${ax} ${from.y} C ${ax} ${lift}, ${bx} ${lift}, ${bx} ${to.y}`,
        x1: ax,
        y1: from.y,
        x2: bx,
        y2: to.y,
      };
    }

    const dx = Math.max(40, (x2 - x1) / 2);
    return {
      path: `M ${x1} ${y1} C ${x1 + dx} ${y1}, ${x2 - dx} ${y2}, ${x2} ${y2}`,
      x1,
      y1,
      x2,
      y2,
    };
  }
}

function movedPastThreshold(
  startX: number,
  startY: number,
  event: PointerEvent,
): boolean {
  return Math.abs(event.clientX - startX) > DRAG_THRESHOLD
    || Math.abs(event.clientY - startY) > DRAG_THRESHOLD;
}

/** Canvas shortcuts never replace the native keyboard behavior of a focused
 *  form control, link, or button. */
function isInteractive(target: EventTarget | null): boolean {
  const element = target instanceof Element ? target : null;
  return !!element?.closest('input, select, textarea, button, a, [contenteditable="true"]');
}
